import React from 'react';
import { AccessoriesRoute } from '|/app/purchase/cart/steps/accessories/route';
import { CarvanaProtectionRoute } from '|/app/purchase/cart/steps/carvana-protection/route';
import { CleanVehicleTaxCreditRoute } from '|/app/purchase/cart/steps/clean-vehicle-tax-credit/route';
import { GapRoute } from '|/app/purchase/cart/steps/gap-coverage/route';
import { IncentivesRoute } from '|/app/purchase/cart/steps/incentives/route';
import { PaymentOptionsRoute } from '|/app/purchase/cart/steps/payment-options/route';
import { PersonalDetailsRoute } from '|/app/purchase/cart/steps/personal-details/route';
import { TradeInRoute } from '|/app/purchase/cart/steps/trade-in/route';
import { TransferMethodRoute } from '|/app/purchase/cart/steps/transfer-method/route';
import { AutopayEnrollmentRoute } from '|/app/purchase/checkout/steps/autopay-enrollment/route';
import { BuyerIdentityRoute } from '|/app/purchase/checkout/steps/buyer-identity/route';
import { CoSignerIdentityRoute } from '|/app/purchase/checkout/steps/co-signer-identity/route';
import { DownPaymentRoute } from '|/app/purchase/checkout/steps/down-payment/route';
import { ReviewOrderRoute } from '|/app/purchase/checkout/steps/review-order/route';
import { DriversLicenseRoute } from '|/app/purchase/legacy/drivers-license/route';
import { IdentityRoute } from '|/app/purchase/legacy/identity/route';
import { PlaceOrderRoute } from '|/app/purchase/legacy/place-order/route';
import { Step, StepName } from '|/app/services';
import { InsuranceOptionsRoute } from './external-routes/insurance-options';
import { OrderPlacedRoute } from './external-routes/order-placed';
import VerifxMfeRoute from './external-routes/verifx-mfe-route';

interface StepRouteProps {
  GetDashboardAndRouteToNextStep: (_?: boolean) => void;
  step?: Step | null;
}

export interface StepRenderProps {
  step: Step | null;
}

type StepConfig = {
  completionTimeEstimate?: number;
} & (
  | { component: React.FC<StepRouteProps> }
  | { override: (props: StepRenderProps) => React.ReactNode }
);

const routeNameComponentLookup: {
  [key in StepName]: StepConfig;
} = {
  [StepName.PersonalDetails]: {
    component: PersonalDetailsRoute,
    completionTimeEstimate: 3
  },
  [StepName.TradeIn]: {
    component: TradeInRoute,
    completionTimeEstimate: 4
  },
  [StepName.Accessories]: {
    component: AccessoriesRoute,
    completionTimeEstimate: 1
  },
  [StepName.CoF]: {
    component: PaymentOptionsRoute,
    completionTimeEstimate: 2
  },
  [StepName.Gap]: {
    component: GapRoute,
    completionTimeEstimate: 2
  },
  [StepName.PickupDelivery]: {
    component: TransferMethodRoute,
    completionTimeEstimate: 1
  },
  [StepName.VSC]: {
    component: CarvanaProtectionRoute,
    completionTimeEstimate: 1
  },
  [StepName.DL]: {
    component: DriversLicenseRoute,
    completionTimeEstimate: 4
  },
  [StepName.Identity]: {
    component: IdentityRoute,
    completionTimeEstimate: 1
  },
  [StepName.Payment]: {
    component: DownPaymentRoute,
    completionTimeEstimate: 3
  },
  [StepName.Autopay]: {
    component: AutopayEnrollmentRoute,
    completionTimeEstimate: 3
  },
  [StepName.Review]: {
    component: ReviewOrderRoute,
    completionTimeEstimate: 2
  },
  [StepName.IdentityVerification]: {
    override: props => <VerifxMfeRoute step={props.step} />,
    completionTimeEstimate: 2
  },
  [StepName.BAV]: {
    override: props => <VerifxMfeRoute step={props.step} />,
    completionTimeEstimate: 2
  },
  [StepName.Income]: {
    override: props => <VerifxMfeRoute step={props.step} />,
    completionTimeEstimate: 5
  },
  [StepName.Address]: {
    override: props => <VerifxMfeRoute step={props.step} />,
    completionTimeEstimate: 5
  },
  [StepName.TradeVerification]: {
    override: props => <VerifxMfeRoute step={props.step} />,
    completionTimeEstimate: 5
  },
  [StepName.AdditionalIncome]: {
    override: props => <VerifxMfeRoute step={props.step} />,
    completionTimeEstimate: 5
  },
  [StepName.CaseSubmission]: {
    override: props => <VerifxMfeRoute step={props.step} />,
    completionTimeEstimate: 1
  },
  [StepName.Schedule]: {
    override: props => <VerifxMfeRoute step={props.step} />,
    completionTimeEstimate: 1
  },
  [StepName.Insurance]: {
    component: InsuranceOptionsRoute,
    completionTimeEstimate: 3
  },
  [StepName.Dashboard]: {
    component: OrderPlacedRoute,
    completionTimeEstimate: 1
  },
  [StepName.CleanVehicleTaxCredit]: {
    component: CleanVehicleTaxCreditRoute,
    completionTimeEstimate: 1
  },
  [StepName.Incentives]: {
    component: IncentivesRoute,
    completionTimeEstimate: 1
  },
  [StepName.PlaceOrder]: {
    component: PlaceOrderRoute
  },
  [StepName.BuyerIdentity]: {
    component: BuyerIdentityRoute,
    completionTimeEstimate: 4
  },
  [StepName.CoSignerIdentity]: {
    component: CoSignerIdentityRoute,
    completionTimeEstimate: 4
  }
};

export default routeNameComponentLookup;
